import React from 'react';
import {DateRangePicker} from 'react-dates';
import moment, {Moment} from 'moment';
import {DateUtils} from '../utility/DateUtils';
import {DateRange} from '../stores/TimeIntervalStore';
import {stylesheet} from 'typestyle';
import {I18n} from '../i18n/i18n';
import {DateSelectorColor, WhiteColor} from '../styles/colors';

interface DateRangeSelectorProps {
    namePrefix: string;
    startDate?: Moment;
    endDate?: Moment;
    onDatesChange: (dateRange: DateRange) => void;
    openDirection?: 'down' | 'up';
    orientation?: 'horizontal' | 'vertical';
    small?: boolean;
    appendToBody?: boolean;
    width?: number | string;
    noBorder?: boolean;
    showRelativeDateRangeButtons?: boolean;
}

interface DateRangeSelectorState {
    focused: 'startDate' | 'endDate' | null;
    startDate?: Moment;
    endDate?: Moment;
}

export default class DateRangeSelector extends React.Component<DateRangeSelectorProps, DateRangeSelectorState> {
    constructor(props: DateRangeSelectorProps) {
        super(props);
        this.state = {
            focused: null,
            startDate: props.startDate,
            endDate: props.endDate,
        };
    }

    public componentWillReceiveProps(nextProps: DateRangeSelectorProps) {
        this.setState({
            startDate: nextProps.startDate,
            endDate: nextProps.endDate,
        });
    }

    /**
     * When the startDate differs from the old startDate, the endDate needs to be removed. This step is necessary, so,
     * that the hover of the daterange picker gets enabled to show the selected interval. If all dates are set, the
     * onDatesChange function gets called with normalized date values.
     * @param {any} startDate
     * @param {any} endDate
     */
    private updateDateRange = ({startDate, endDate}: {startDate: Moment | null; endDate: Moment | null}) => {
        const clonedStartDate = startDate || undefined; // DateRangePicker emits null instead of undefined
        let clonedEndDate = endDate || undefined; // DateRangePicker emits null instead of undefined

        if (!DateUtils.dateIsSame(clonedStartDate, this.state.startDate) && DateUtils.dateIsSame(clonedEndDate, this.state.endDate)) {
            clonedEndDate = undefined;
        }
        this.setState({
            startDate: clonedStartDate,
            endDate: clonedEndDate,
        });
        if (clonedStartDate !== undefined && clonedEndDate !== undefined) {
            this.props.onDatesChange({startDate: moment(clonedStartDate), endDate: moment(clonedEndDate)});
        }
    };

    private setYesterdayDateRange = () => {
        const yesterday = moment().subtract(1, 'days');
        this.setDateRange({startDate: yesterday, endDate: yesterday});
    };

    private setDateRange = (dateRange: DateRange) => {
        this.setState(dateRange);
        this.props.onDatesChange(dateRange);
    };

    private setThisDateRange(interval: 'week' | 'month') {
        const startDate = moment().startOf(interval);
        const endDate = moment().endOf(interval);
        this.setState({
            startDate,
            endDate,
        });
        this.props.onDatesChange({startDate, endDate});
    }

    private setLastDateRange(interval: 'week' | 'month') {
        const startDate = moment()
            .subtract(1, interval)
            .startOf(interval);
        const endDate = moment()
            .subtract(1, interval)
            .endOf(interval);
        this.setState({
            startDate,
            endDate,
        });
        this.props.onDatesChange({startDate, endDate});
    }

    private setLastXDays(days: number) {
        const endDate = moment().subtract(1, 'days');
        const startDate = moment().subtract(1 + days, 'days');
        this.setState({
            startDate,
            endDate,
        });
        this.props.onDatesChange({startDate, endDate});
    }

    private renderRelativeDateRangeButtons = (): React.ReactElement<Element> => {
        if (!this.props.showRelativeDateRangeButtons) {
            return <div />;
        }

        return (
            <div className={dateRangeCss.buttonContainer}>
                <button onClick={this.setYesterdayDateRange} className={dateRangeCss.relativeDateButton}>
                    <I18n i18nKey="datepickerButtons.yesterday" />
                </button>
                <button onClick={() => this.setThisDateRange('week')} className={dateRangeCss.relativeDateButton}>
                    <I18n i18nKey="datepickerButtons.thisWeek" />
                </button>
                <button onClick={() => this.setLastDateRange('week')} className={dateRangeCss.relativeDateButton}>
                    <I18n i18nKey="datepickerButtons.lastWeek" />
                </button>
                <button onClick={() => this.setThisDateRange('month')} className={dateRangeCss.relativeDateButton}>
                    <I18n i18nKey="datepickerButtons.thisMonth" />
                </button>
                <button onClick={() => this.setLastDateRange('month')} className={dateRangeCss.relativeDateButton}>
                    <I18n i18nKey="datepickerButtons.lastMonth" />
                </button>
                <button onClick={() => this.setLastXDays(7)} className={dateRangeCss.relativeDateButton}>
                    <I18n i18nKey="datepickerButtons.lastSevenDays" />
                </button>
                <button onClick={() => this.setLastXDays(30)} className={dateRangeCss.relativeDateButton}>
                    <I18n i18nKey="datepickerButtons.lastThirtyDays" />
                </button>
            </div>
        );
    };

    public render(): React.ReactNode {
        return (
            <div className={dateRangeCss.root} style={{width: this.props.width || 320}}>
                <DateRangePicker
                    hideKeyboardShortcutsPanel={true}
                    renderCalendarInfo={this.renderRelativeDateRangeButtons}
                    block={true}
                    noBorder={this.props.noBorder}
                    appendToBody={this.props.appendToBody}
                    small={this.props.small}
                    orientation={this.props.orientation}
                    openDirection={this.props.openDirection}
                    minimumNights={0}
                    startDate={this.state.startDate || null}
                    startDateId={`${this.props.namePrefix}-dateStart`}
                    endDate={this.state.endDate || null}
                    endDateId={`${this.props.namePrefix}-dateEnd`}
                    displayFormat={moment.localeData().longDateFormat('L')}
                    isOutsideRange={() => false}
                    onDatesChange={this.updateDateRange}
                    focusedInput={this.state.focused}
                    onFocusChange={(focusedInput) => this.setState({focused: focusedInput})}
                />
            </div>
        );
    }
}

const dateRangeCss = stylesheet({
    root: {
        pointerEvents: 'all',
    },
    buttonContainer: {
        padding: '0px 17px 11px',
        display: 'flex',
        flexWrap: 'wrap',
    },
    relativeDateButton: {
        marginTop: 5,
        marginRight: 5,
        whiteSpace: 'nowrap',
        backgroundColor: 'transparent',
        color: DateSelectorColor,
        border: '2px solid ' + DateSelectorColor,
        borderRadius: 2,
        transition: '.2s',
        $nest: {
            '&:hover': {
                backgroundColor: DateSelectorColor,
                color: WhiteColor,
            },
        },
    },
});



// WEBPACK FOOTER //
// ./src/views/DateRangeSelector.tsx